import { MutationFunction, UseMutateFunction, useMutation } from '@tanstack/react-query';

import { createUploaderFileFromClaimFile } from '../../../claims-center/submit-claims/createClaimContext';
import { ClaimFile, ImportFileResponse } from '../../../../api/types';
import { ClaimContextFiles } from '../types';

export type UseFileImportOptions<TVariables> = {
  shopId: number;
  fileKey: keyof ClaimContextFiles;
  onImportFile: MutationFunction<ImportFileResponse, TVariables>;
  onChange: (data: Record<string, unknown>) => void;
  onSuccess?: (data: ClaimFile) => void;
};

export type ImportFileState = 'idle' | 'searching' | 'error' | 'found' | 'not_found';

export function useFileImport<TVariables>(
  options: UseFileImportOptions<TVariables>
): [UseMutateFunction<ImportFileResponse, unknown, TVariables>, ImportFileState] {
  const { shopId, onImportFile, onChange, onSuccess, fileKey } = options;

  const { mutate, isLoading, isError, isIdle, data } = useMutation({
    mutationKey: ['postClaimsImport', shopId, fileKey],
    mutationFn: onImportFile,
    onSuccess: (data) => {
      const { file: claimFile } = data;

      if (claimFile) {
        onChange({ [fileKey]: [createUploaderFileFromClaimFile(claimFile)] });

        if (onSuccess) {
          onSuccess(claimFile);
        }
      }
    },
  });

  if (isIdle) {
    return [mutate, 'idle'];
  }

  if (isLoading) {
    return [mutate, 'searching'];
  }

  if (isError) {
    return [mutate, 'error'];
  }

  return [mutate, data?.status === 'found' ? 'found' : 'not_found'];
}
